import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Globe, Eye, TrendingUp, BarChart3, Flag, Percent, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ApplyOnlineAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      
      const response = await axios.get(
        `${BACKEND_URL}/api/analytics/admin/apply-online`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching Apply Online analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <RefreshCw size={32} className="spinning" style={{ color: '#A08EA8', marginBottom: '12px' }} />
        <p style={{ color: '#6b7280' }}>Loading Apply Online Analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#dc2626' }}>
        <p>{error}</p>
        <button 
          onClick={fetchAnalytics}
          style={{ 
            marginTop: '12px', 
            padding: '8px 16px', 
            background: '#A08EA8', 
            color: 'white', 
            border: 'none', 
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!analytics) return null;

  const { summary, top_countries, country_breakdown } = analytics;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Globe size={24} style={{ color: '#A08EA8' }} />
          Apply Online Page Analytics
        </h2>
        <p style={{ color: '#6b7280', fontSize: '14px' }}>
          Track visitor engagement with visa country cards on the Apply Online page
        </p>
      </div>

      {/* Summary Stats Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px',
        marginBottom: '32px'
      }}>
        {/* Total Views */}
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Eye size={20} />
            <span style={{ fontSize: '13px', opacity: 0.9 }}>Total Country Views</span>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700' }}>
            {(summary.total_country_views || 0).toLocaleString()}
          </div>
        </div>

        {/* Total Countries */}
        <div style={{ 
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Flag size={20} />
            <span style={{ fontSize: '13px', opacity: 0.9 }}>Active Countries</span>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700' }}>
            {summary.total_countries || 0}
          </div>
        </div>

        {/* Average Views */}
        <div style={{ 
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <BarChart3 size={20} />
            <span style={{ fontSize: '13px', opacity: 0.9 }}>Avg Views/Country</span>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700' }}>
            {summary.average_views_per_country || 0}
          </div>
        </div>

        {/* Top Country */}
        <div style={{ 
          background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: '#1f2937'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <TrendingUp size={20} />
            <span style={{ fontSize: '13px', opacity: 0.8 }}>Top Country</span>
          </div>
          <div style={{ fontSize: '20px', fontWeight: '700' }}>
            {summary.top_country || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
            {(summary.top_country_views || 0).toLocaleString()} views
          </div>
        </div>
      </div>

      {/* Top 10 Countries */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TrendingUp size={18} style={{ color: '#A08EA8' }} />
          Top 10 Countries by Views
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', 
          gap: '12px' 
        }}>
          {top_countries.map((country, index) => (
            <div 
              key={country.country_code}
              style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}
            >
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: index < 3 ? ['#ffd700', '#c0c0c0', '#cd7f32'][index] : '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: '14px',
                color: index < 3 ? '#1f2937' : '#6b7280'
              }}>
                {index + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '2px' }}>
                  {country.flag_url && (
                    <img 
                      src={country.flag_url} 
                      alt="" 
                      style={{ width: '16px', height: '12px', marginRight: '6px', borderRadius: '2px' }}
                    />
                  )}
                  {country.country_name}
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#A08EA8' }}>
                  {(country.view_count || 0).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Full Country Breakdown Table */}
      <div>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BarChart3 size={18} style={{ color: '#A08EA8' }} />
          Complete Country Breakdown
        </h3>
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          border: '1px solid #e5e7eb',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Rank
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Country
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Visa Type
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Processing Time
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Views
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  % Share
                </th>
              </tr>
            </thead>
            <tbody>
              {country_breakdown.map((country, index) => (
                <tr 
                  key={country.id}
                  style={{ 
                    borderTop: '1px solid #e5e7eb',
                    background: index % 2 === 0 ? 'white' : '#fafafa'
                  }}
                >
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#6b7280' }}>
                    #{index + 1}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {country.flag_url && (
                        <img 
                          src={country.flag_url} 
                          alt="" 
                          style={{ width: '24px', height: '16px', borderRadius: '2px', border: '1px solid #e5e7eb' }}
                        />
                      )}
                      <span style={{ fontWeight: '500', fontSize: '14px' }}>{country.country_name}</span>
                      <span style={{ 
                        fontSize: '10px', 
                        background: '#f3f4f6', 
                        padding: '2px 6px', 
                        borderRadius: '4px',
                        color: '#6b7280'
                      }}>
                        {country.country_code}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>
                    {country.visa_type || 'Tourist Visa'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>
                    {country.processing_time || 'N/A'}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <span style={{ 
                      fontWeight: '700', 
                      fontSize: '16px', 
                      color: country.view_count > 0 ? '#16a34a' : '#9ca3af' 
                    }}>
                      {(country.view_count || 0).toLocaleString()}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                      <div style={{ 
                        width: '60px', 
                        height: '6px', 
                        background: '#e5e7eb', 
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{ 
                          width: `${Math.min(country.percentage, 100)}%`, 
                          height: '100%', 
                          background: 'linear-gradient(90deg, #A08EA8, #667eea)',
                          borderRadius: '3px'
                        }} />
                      </div>
                      <span style={{ fontSize: '13px', color: '#6b7280', minWidth: '45px' }}>
                        {country.percentage}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {country_breakdown.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              No country data available. Add visa products to see analytics.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
